/**
 * Location and keyword landing pages.
 *
 * These carried most of the old site's local search traffic, so they keep their
 * exact URLs. Headings follow the originals; each points at the service page
 * that does the actual work.
 */
export interface LocationSection {
  heading: string;
  body: string;
}

export interface LocationContent {
  slug: string;
  h1: string;
  metaTitle: string;
  metaDescription: string;
  intro: string;
  sections: LocationSection[];
  /** Service slugs to surface as follow-on reading. */
  related: string[];
}

export const LOCATIONS: LocationContent[] = [
  {
    slug: 'car-wash-hamilton',
    h1: 'Car Wash Hamilton',
    metaTitle: 'Car Wash Hamilton | Fine Shine Car Wash',
    metaDescription:
      'Fine Shine Car Wash offers top-notch car wash Hamilton services. A properly clean car inside and out, washed at your home or office.',
    intro:
      'Fine Shine Car Wash provides professional cleaning that protects your vehicle from environmental damage, and does it using far less water than washing at home.',
    sections: [
      {
        heading: 'Unmatched shine every time',
        body: 'Washing regularly is not only about appearance. Clean glass and lights mean better visibility, and going over the vehicle each time is how small problems get spotted before they become expensive ones.',
      },
      {
        heading: "Hamilton's premier car wash experience",
        body: 'Full service means exterior treatments such as waxing and tyre shine alongside interior work like vacuuming and detailing, rather than one at the expense of the other.',
      },
      {
        heading: 'Elevate your ride',
        body: 'Professional tyre care, ceramic coating protection and headlight restoration all add durability and safety on top of the obvious visual lift.',
      },
      {
        heading: 'Comprehensive care',
        body: 'A full clean removes dust, bacteria and allergens from the cabin, which makes the car a healthier place to sit as well as a better looking one.',
      },
    ],
    related: ['exterior-car-wash', 'interior-car-wash', 'car-detailing'],
  },
  {
    slug: 'mobile-car-wash-hamilton',
    h1: 'Mobile Car Wash Hamilton',
    metaTitle: 'Mobile Car Wash Hamilton | Fine Shine Car Wash',
    metaDescription:
      'The best mobile car wash Hamilton service. Our technicians come to you with everything needed, including water and power. Eco-friendly products.',
    intro:
      'Doorstep car cleaning using eco-friendly products and skilled technicians who handle both interior and exterior detailing — built around people who do not have half a day to spare.',
    sections: [
      {
        heading: 'Shine on your terms',
        body: 'Professional detailing delivered to your location, with proper attention to paint, glass and upholstery rather than a rushed once-over.',
      },
      {
        heading: 'Washing at your door',
        body: 'We arrive fully equipped with our own water and power, so nothing is needed from you. Eco-friendly materials mean high quality results without the environmental cost.',
      },
      {
        heading: 'Personalised care',
        body: 'Hand washing with a carefully selected range of high quality products, matched to your vehicle rather than run through an automated process that treats every car the same.',
      },
      {
        heading: 'Affordable and transparent',
        body: 'Pricing is published by vehicle size with no hidden fees, from a basic wash through to a full deep clean.',
      },
    ],
    related: ['exterior-car-wash', 'interior-car-wash', 'car-detailing'],
  },
  {
    slug: 'hand-car-wash-hamilton',
    h1: 'Hand Car Wash Hamilton',
    metaTitle: 'Hand Car Wash Hamilton | Fine Shine Car Wash',
    metaDescription:
      'Hand car wash in Hamilton. Our skilled team pays close attention to every detail, washing by hand with premium eco-friendly products.',
    intro:
      'Our skilled team pays close attention to every detail so that your vehicle receives personalised attention and a thorough cleaning by hand.',
    sections: [
      {
        heading: 'The Fine Shine touch',
        body: 'Hand washing with premium, eco-friendly products, adjusted to the vehicle in front of us rather than a fixed routine.',
      },
      {
        heading: 'Where quality speaks',
        body: 'A gentle yet thorough cleaning experience that preserves your vehicle’s finish and shine — which is exactly what automated brushes fail to do.',
      },
      {
        heading: 'Top quality, fair cost',
        body: 'Transparent, affordable pricing with no hidden fees, and flexible packages so you only pay for the level of clean you actually want.',
      },
    ],
    related: ['exterior-car-wash', 'waxing-polishing'],
  },
  {
    slug: 'car-valet-hamilton',
    h1: 'Car Valet Hamilton',
    metaTitle: 'Car Valet Hamilton | Fine Shine Car Wash',
    metaDescription:
      'Car valet services in Hamilton. Comprehensive detailing that goes well beyond a standard wash, at your home or workplace.',
    intro:
      'Comprehensive vehicle detailing that goes beyond a standard wash, built for Hamilton weather and urban driving, and aimed at extending how long your vehicle stays in good condition.',
    sections: [
      {
        heading: 'The secret to a gleaming, polished vehicle',
        body: 'Mobile convenience, expert detailing and customisable packages, with the kind of attention that shows up in the details rather than just the panels.',
      },
      {
        heading: 'The ultimate care solution',
        body: 'Better appearance, preserved resale value, a nicer car to drive and a longer working life — the reasons valeting is worth doing on a schedule rather than once in a while.',
      },
      {
        heading: 'Why look no further',
        body: 'An experienced team, eco-friendly products, transparent pricing, flexible scheduling and service that does not disappear once the invoice is paid.',
      },
    ],
    related: ['car-detailing', 'interior-car-wash', 'ceramic-coating'],
  },
  {
    slug: 'car-exterior-cleaning-hamilton',
    h1: 'Car Exterior Cleaning Hamilton',
    metaTitle: 'Car Exterior Cleaning Hamilton | Fine Shine Car Wash',
    metaDescription:
      'Professional car exterior cleaning in Hamilton. From eco washes to multi-level paint correction and ceramic coating treatments.',
    intro:
      'Hamilton roads and weather are hard on paintwork. Road grime, dust and environmental stressors take the edge off both appearance and value if they are left to sit.',
    sections: [
      {
        heading: 'Steam clean car exterior',
        body: 'High pressure steam lifts dirt without harsh chemicals, and reaches the awkward places a sponge never does — wheel wells, door jambs and shuts.',
      },
      {
        heading: 'Expert care beats DIY',
        body: 'Professional detailing gives a better result, takes back your weekend, and protects resale value in a way a bucket and hose does not.',
      },
      {
        heading: 'The full process',
        body: 'Washing, clay bar treatment, polishing, sealant application, headlight restoration and wheel cleaning, staged in the right order so each step builds on the last.',
      },
    ],
    related: ['exterior-car-wash', 'waxing-polishing', 'ceramic-coating'],
  },
  {
    slug: 'car-interior-cleaning-hamilton',
    h1: 'Car Interior Cleaning Hamilton',
    metaTitle: 'Car Interior Cleaning Hamilton | Fine Shine Car Wash',
    metaDescription:
      'Car interior cleaning in Hamilton. Eco-friendly steam methods, fast drying times and experienced technicians. Mobile service to your door.',
    intro:
      'Your car is a personal space, but daily use brings in dirt, spills and debris that no amount of tidying quite fixes. Interior cleaning resets it properly.',
    sections: [
      {
        heading: 'How to achieve pristine interiors',
        body: 'Experienced technicians using eco-friendly steam methods with fast drying times, so the car is back in use the same day.',
      },
      {
        heading: 'What sets steam cleaning apart',
        body: 'Steam blasts away dirt and grime, neutralises odours and sanitises surfaces, all while being gentle on delicate materials that chemicals would damage.',
      },
      {
        heading: 'What it costs',
        body: 'Price depends on vehicle size and how deep a clean is needed. We give a fair quote up front rather than a number that moves once we arrive.',
      },
      {
        heading: 'Dry cleaning as an option',
        body: 'Where water is best avoided, dry cleaning gives a faster drying time and is gentler on certain fabrics.',
      },
    ],
    related: ['interior-car-wash', 'full-interior-steam-clean', 'shampoo-leather-treatment'],
  },
  {
    slug: 'car-wax-and-polish-hamilton',
    h1: 'Car Wax and Polish Hamilton',
    metaTitle: 'Car Wax and Polish Hamilton | Fine Shine Car Wash',
    metaDescription:
      'Car wax and polish in Hamilton. Restore shine lost to road dust and everyday wear, and protect the paint underneath for months afterwards.',
    intro:
      'Road dust and everyday wear dull a finish faster than most people expect. Waxing and polishing restores the shine and leaves lasting protection behind it.',
    sections: [
      {
        heading: 'Ultimate gloss and protection',
        body: 'Polish and wax do different jobs. Polish removes the imperfections in the paint; wax lays a protective shield over the result.',
      },
      {
        heading: 'The benefits',
        body: 'A genuinely dazzling shine, better protection from the elements, easier washing afterwards, and a resale value that holds up.',
      },
      {
        heading: 'Black paint needs a different approach',
        body: 'Dark colours show every swirl and water spot, so they need specific products and a more careful finishing stage.',
      },
      {
        heading: 'How we do it',
        body: 'A thorough pre-wash, then correction, then protection, then final buffing — skipping the first step is why so many quick jobs end up adding scratches.',
      },
    ],
    related: ['waxing-polishing', 'paint-correction', 'ceramic-coating'],
  },
  {
    slug: 'ceramic-coating-hamilton',
    h1: 'Ceramic Coating Hamilton',
    metaTitle: 'Ceramic Coating Hamilton | Fine Shine Car Wash',
    metaDescription:
      'Ceramic coating in Hamilton. Advanced liquid polymer bonds to your paint for a lasting protective layer, deeper gloss and easier washing.',
    intro:
      'Ceramic coating uses advanced liquid polymer technology that bonds to the paint and forms a protective layer over it — considerably longer lasting than traditional wax.',
    sections: [
      {
        heading: 'Why choose ceramic coating',
        body: 'Ultimate shine and protection, less maintenance between washes, and value preservation over the years you own the car.',
      },
      {
        heading: 'Glossy finish, lasting shine',
        body: 'A liquid polymer substance that outperforms traditional wax on both durability and depth of gloss.',
      },
      {
        heading: 'Affordable shielding',
        body: 'It costs more up front than a wax, but lasts far longer and cuts down the maintenance in between, which is where the value shows.',
      },
      {
        heading: 'Restore and protect',
        body: 'Coating locks in whatever is underneath, so scratches and swirls are corrected first. Applied by someone who does it regularly, it holds up against merciless sun and unpredictable weather.',
      },
    ],
    related: ['ceramic-coating', 'paint-correction', 'waxing-polishing'],
  },
  {
    slug: 'paint-correction-hamilton',
    h1: 'Paint Correction Hamilton',
    metaTitle: 'Paint Correction Hamilton | Cut and Polish | Fine Shine Car Wash',
    metaDescription:
      'Paint correction in Hamilton. Our detailers remove scratches, swirls and oxidation, leaving a flawless, showroom-quality finish.',
    intro:
      'Dull or scratched paint restored properly. Our meticulous detailers remove scratches, swirls and oxidation, leaving a flawless, showroom-quality finish.',
    sections: [
      {
        heading: 'What the service includes',
        body: 'Scratch removal, oxidation removal, water spot removal and a clear improvement in gloss and clarity, staged over one or two passes depending on the paint.',
      },
      {
        heading: 'What it costs',
        body: 'Pricing depends on the severity of the damage and the size of the vehicle. Machine polish starts from $260 and full paint correction from $300, quoted properly after we have seen the car.',
      },
      {
        heading: 'New cars too',
        body: 'Freshly delivered vehicles often carry dealer preparation marks. Correcting them early sets the paint up for the rest of its life.',
      },
      {
        heading: 'Black paint',
        body: 'Black shows everything, so it needs specialised technique to finish without leaving haze or holograms behind.',
      },
    ],
    related: ['paint-correction', 'ceramic-coating', 'waxing-polishing'],
  },
];

export const LOCATION_BY_SLUG = new Map(LOCATIONS.map((location) => [location.slug, location]));
